import { Button, Input, Select, Space, TableProps } from "antd";
import { useEffect, useState } from "react";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import SortIcon from "../../../../assets/icons/SortIcon";
import CustomModal from "../../../../components/ui/CustomModal";
import CustomTable from "../../../../components/ui/CustomTable";
import { useGetDepartmentsQuery } from "../../../../features/department/departmentApi";
import { useGetDesignationsQuery } from "../../../../features/designation/designationApi";
import { useGetEmployeesQuery } from "../../../../features/employee/employeeApi";
import useError from "../../../../hooks/useError";
import useTable from "../../../../hooks/useTable";
import { toast } from "../../../../lib/toast";
import {
  EmployeeData,
  GetEmployeeParamsType,
  ModalComponentProps,
} from "../../../../types";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
  underscoreToCapitalizeString,
} from "../../../../utils/utils";

type AdditionsAddEmployeeModalTypes = {
  onChange: (employees: EmployeeData[]) => void;
} & ModalComponentProps<EmployeeData[]>;

const searchTypeSelect = ["name", "email", "employee_id", "mobile"]?.map(
  (item) => ({
    label: underscoreToCapitalizeString(item),
    value: item,
  })
);

export default function AdditionsAddEmployeeModal({
  data: dataIds,
  handleClose,
  onChange,
  ...rest
}: AdditionsAddEmployeeModalTypes) {
  const [selectedData, setSelectedData] = useState<EmployeeData[]>([]);
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    keyword,
    handleFilter,
    handleSearch,
  } = useTable<GetEmployeeParamsType>({
    defaultFilter: {
      status: "active",
      search_type: "name",
    },
  });

  const { isLoading, isError, error, data, isFetching } = useGetEmployeesQuery({
    params: {
      ...filter,
      search_type: undefined,
      ...(keyword ? { search_type: filter.search_type } : {}),
      search: keyword,
      status: "active",
    },
  });
  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    data: designationData,
    isError: isDesignationError,
    error: designationError,
  } = useGetDesignationsQuery(
    {
      params: {
        limit: 0,
        skip: 0,
        ...(filter?.business_department_id
          ? {
              business_department_id:
                filter?.business_department_id?.toString() || "",
            }
          : {}),
        is_published: 1,
      },
    },
    {
      skip: !filter?.business_department_id,
    }
  );
  useError(isError, error, "Get employee data fail");
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isDesignationError, designationError, "Get designation fail");

  const columns: TableProps<EmployeeData>["columns"] = [
    {
      title: "Employee Id",
      dataIndex: "employee_id",
      sorter: (a, b) =>
        Number(a?.employee_id ?? 0) - Number(b?.employee_id ?? 0),
      sortIcon: () => <SortIcon />,
      render: (val, record) => (
        <span className="flex items-center gap-1.5">{val ?? "n/a"}</span>
      ),
    },
    {
      title: "Employee Name",
      dataIndex: "member",
      sorter: (a, b) =>
        a?.member?.profile?.name?.localeCompare(b?.member?.profile?.name),
      sortIcon: () => <SortIcon />,
      render: (val) => val?.profile?.name ?? "n/a",
    },
    {
      title: "Email ID",
      dataIndex: "member",
      sorter: (a, b) =>
        a?.member?.profile?.email?.localeCompare(b?.member?.profile?.email),
      sortIcon: () => <SortIcon />,
      render: (val) => val?.profile?.email ?? "n/a",
    },
  ];

  const handleAdd = () => {
    if (!selectedData?.length) {
      toast("error", "Please select at least one employee");
      return;
    }

    const combined = dataIds?.length
      ? [...dataIds, ...selectedData]
      : selectedData;

    const unique = Array.from(
      new Map(combined.map((item) => [item.id, item])).values()
    );

    onChange(unique);
    toast("success", "Successfully added!");
    handleClose();
  };

  useEffect(() => {
    if (dataIds?.length) {
      setSelectedData(dataIds);
    }
  }, [dataIds]);

  return (
    <CustomModal
      footer={null}
      closeIcon={null}
      width={900}
      className="modal-padding-hide"
      handleClose={handleClose}
      {...rest}
    >
      {/* <Space
        wrap
        className="p-4 w-full justify-between sticky top-0 z-10 bg-white"
      >
        <h4 className="text-sub-header text-[22px] font-semibold leading-[33px]">
          Leave History
        </h4>
        <Space wrap size={10}>
          <Select
            allowClear
            showSearch
            placeholder="Department"
            filterOption={selectSearchOption}
            options={selectArrayOption(
              departmentData?.data?.departments || [],
              "name",
              "id"
            )}
            className="min-w-[130px]"
            suffixIcon={<SelectDownIcon />}
            rootClassName="icon-gray"
            onChange={(val) => {
              handleFilter("business_department_id", val);
              handleFilter("business_role_id", undefined);
            }}
            size="middle"
          />
          <Select
            allowClear
            showSearch
            placeholder="Designation"
            filterOption={selectSearchOption}
            options={selectArrayOption(
              designationData?.data?.roles || [],
              "name",
              "id"
            )}
            className="min-w-[130px]"
            suffixIcon={<SelectDownIcon />}
            rootClassName="icon-gray"
            onChange={(val) => handleFilter("business_role_id", val)}
            disabled={!filter.business_department_id}
            size="middle"
          />
        </Space>
      </Space> */}

      <CustomTable
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.totalLength}
        dataSource={addKeyInArray(data?.data?.employees)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: selectedData?.map((item) => item?.id),
          onChange: (_, record) => {
            setSelectedData(record as EmployeeData[]);
          },
          // getCheckboxProps: (record) => {
          //   return {
          //     disabled: !!dataIds?.find((item) => item === record?.id),
          //   };
          // },
          columnWidth: 40,
        }}
        filterSection={
          <>
            <Select
              showSearch
              placeholder="Search Type"
              value={filter.search_type}
              options={searchTypeSelect}
              onChange={(val) => handleFilter("search_type", val)}
              filterOption={selectSearchOption}
              className="min-w-[100px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              popupMatchSelectWidth={false}
              size="middle"
            />
            <Input
              key={filter.search_type}
              suffix={<SearchIcon />}
              placeholder={`Search by ${underscoreToCapitalizeString(
                filter.search_type || "",
                true
              )}`}
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
              size="middle"
            />
            <Select
              allowClear
              showSearch
              placeholder="Department"
              filterOption={selectSearchOption}
              options={selectArrayOption(
                departmentData?.data?.departments || [],
                "name",
                "id"
              )}
              className="min-w-[130px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              onChange={(val) => {
                handleFilter("business_department_id", val);
                handleFilter("business_role_id", undefined);
              }}
              size="middle"
            />
            <Select
              allowClear
              showSearch
              placeholder="Designation"
              filterOption={selectSearchOption}
              options={selectArrayOption(
                designationData?.data?.roles || [],
                "name",
                "id"
              )}
              className="min-w-[130px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              onChange={(val) => handleFilter("business_role_id", val)}
              disabled={!filter.business_department_id}
              size="middle"
            />
          </>
        }
        topSectionClassName="sticky top-0 z-10 bg-white"
      />

      <Space className="w-full p-4 sticky bottom-0 bg-white" size={12}>
        <Button
          className="normal-dark-button modal-button"
          onClick={handleClose}
          size="middle"
        >
          Cancel
        </Button>
        <Button
          className="modal-button"
          type="primary"
          size="middle"
          onClick={handleAdd}
        >
          Add
        </Button>
      </Space>
    </CustomModal>
  );
}
